/**
 * Leviathan - Tax Configuration
 * Quasar Prime LLC
 *
 * Where a customer maintains their own tax rates.
 *
 * That is the entire point of the engine's design: nothing about any
 * jurisdiction is hardcoded, so a rate change is something an operator enters
 * the morning it takes effect rather than a release somebody has to ship. If an
 * operator cannot do what they need here, they are back on the phone and the
 * design has failed.
 *
 * TWO THINGS THIS SCREEN HAS TO EXPLAIN, NOT JUST DO:
 *
 *   1. An edit creates a NEW VERSION rather than changing the old one. Every
 *      admin expects edit-in-place and will be surprised. The surprise is
 *      cheaper than the alternative: the moment history is editable, a
 *      historical invoice stops being reproducible and last year's return
 *      silently disagrees with last year's invoices.
 *
 *   2. Seeded rates are a starting point, not a guarantee. A stale seeded rate
 *      that a customer files a return against is a liability question, not just
 *      a bug — so the disclaimer is prominent and permanent, not a dismissible
 *      toast.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronRight,
  History,
  Info,
  Loader2,
  Plus,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { API_URL } from '@/config/api';
import { extractMessage } from '@/utils/formErrors';
import { parseApiError } from '@/utils/errorHandler';

interface RateVersion {
  id: string;
  rate: string;
  formula: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  lastVerifiedAt: string | null;
  verifiedBy: string | null;
  source: string | null;
}

interface TaxDefinitionRow {
  id: string;
  code: string;
  name: string;
  category: string;
  rateKind: string;
  volumeBasis: string;
  defaultTreatment: string;
  isActive: boolean;
  jurisdiction: { id: string; code: string; name: string; level: string };
  currentRate: RateVersion | null;
  versionCount: number;
  suspension: { id: string; suspendedFrom: string; suspendedTo: string | null; reason: string } | null;
}

interface Certificate {
  id: string;
  certificateNumber: string;
  exemptionType: string;
  expiresAt: string | null;
  status: 'CURRENT' | 'EXPIRING_SOON' | 'EXPIRED';
  customer: { id: string; name: string; customerNumber: string } | null;
}

type Tab = 'taxes' | 'certificates' | 'indices';

const VOLUME_BASIS_LABEL: Record<string, string> = {
  NET_GALLONS: 'Net gallons (60°F)',
  GROSS_GALLONS: 'Gross gallons (ambient)',
  BILLED_GALLONS: 'Billed gallons',
  METERED_VOLUME: 'Metered volume',
  DOLLAR_AMOUNT: 'Dollar amount',
  PER_TRANSACTION: 'Per transaction',
  PER_LOAD: 'Per load',
};

const RATE_KIND_LABEL: Record<string, string> = {
  FLAT_PER_UNIT: 'Flat per unit',
  PERCENTAGE: 'Percentage',
  TIERED: 'Tiered',
  FORMULA: 'Formula / indexed',
};

export default function TaxConfiguration() {
  const { accessToken } = useAuth();
  const { isAdministrator, can } = usePermissions();
  const canEdit = isAdministrator || can('tax:edit');

  const [tab, setTab] = useState<Tab>('taxes');
  const [definitions, setDefinitions] = useState<TaxDefinitionRow[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, RateVersion[]>>({});
  const [newVersionFor, setNewVersionFor] = useState<TaxDefinitionRow | null>(null);
  const [suspendFor, setSuspendFor] = useState<TaxDefinitionRow | null>(null);

  const headers = useCallback(
    (json = false) => ({
      Authorization: `Bearer ${accessToken}`,
      ...(json ? { 'Content-Type': 'application/json' } : {}),
    }),
    [accessToken]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defsRes, certRes] = await Promise.all([
        fetch(`${API_URL}/api/tax/config/definitions`, { headers: headers() }),
        fetch(`${API_URL}/api/tax/config/certificates`, { headers: headers() }),
      ]);
      if (!defsRes.ok) throw new Error(await parseApiError(defsRes));
      if (!certRes.ok) throw new Error(await parseApiError(certRes));

      setDefinitions((await defsRes.json()).definitions);
      setCertificates((await certRes.json()).certificates);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tax configuration');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const openHistory = async (definition: TaxDefinitionRow) => {
    if (expanded === definition.id) {
      setExpanded(null);
      return;
    }
    setExpanded(definition.id);
    if (history[definition.id]) return;

    const res = await fetch(`${API_URL}/api/tax/config/definitions/${definition.id}`, {
      headers: headers(),
    });
    if (res.ok) {
      const data = await res.json();
      setHistory((prev) => ({ ...prev, [definition.id]: data.definition.rateVersions }));
    }
  };

  const verifyRate = async (versionId: string) => {
    const res = await fetch(`${API_URL}/api/tax/config/versions/${versionId}/verify`, {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({}),
    });
    if (res.ok) {
      setBanner('Rate marked as verified today.');
      await load();
    } else {
      setError(await parseApiError(res));
    }
  };

  const reinstate = async (suspensionId: string) => {
    const res = await fetch(`${API_URL}/api/tax/config/suspensions/${suspensionId}/reinstate`, {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({}),
    });
    if (res.ok) {
      setBanner('Tax reinstated. The suspension period stays in history.');
      await load();
    } else {
      setError(await parseApiError(res));
    }
  };

  const staleCount = certificates.filter((c) => c.status !== 'CURRENT').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Tax Configuration</h1>
        <p className="text-slate-400 text-sm mt-1">
          Rates, jurisdictions, and exemption certificates for this instance.
        </p>
      </div>

      {/* PERMANENT and not dismissible. A stale seeded rate that a customer
          files a return against is a liability question, not just a bug. */}
      <div className="flex items-start space-x-3 bg-amber-500/10 border border-amber-500/50 p-4">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-slate-300">
          <p className="font-medium text-amber-400 mb-1">
            You are responsible for verifying these rates
          </p>
          <p>
            Any rates shipped with Leviathan are a <strong>starting point only</strong>. Tax rates
            change by legislative session and some states index them. Check every rate against your
            own jurisdictions before filing anything, and use <em>Mark verified</em> to record when
            you last did. Leviathan calculates what you configure — it does not warrant that what
            you configured is correct.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start justify-between bg-red-500/10 border border-red-500/50 p-4 text-red-300 text-sm">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {banner && (
        <div className="flex items-start justify-between bg-green-500/10 border border-green-500/50 p-4 text-green-300 text-sm">
          <span>{banner}</span>
          <button onClick={() => setBanner(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {staleCount > 0 && (
        <div className="flex items-start space-x-3 bg-red-500/10 border border-red-500/50 p-4">
          <CalendarClock className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-300">
            <p className="font-medium text-red-400 mb-1">
              {staleCount} exemption certificate{staleCount === 1 ? '' : 's'} expired or expiring
            </p>
            <p>
              An expired certificate does <strong>not</strong> exempt a customer, and invoices
              raised against one are taxable. An auditor will ask.{' '}
              <button
                onClick={() => setTab('certificates')}
                className="text-amber-500 hover:underline"
              >
                Review them
              </button>
            </p>
          </div>
        </div>
      )}

      <div className="flex space-x-1 border-b border-slate-700">
        {(
          [
            ['taxes', 'Taxes & Rates'],
            ['certificates', `Exemption Certificates${staleCount ? ` (${staleCount})` : ''}`],
            ['indices', 'Index Values'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === key
                ? 'border-amber-500 text-amber-500'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'taxes' && (
        <div className="space-y-3">
          <div className="flex items-start space-x-3 bg-slate-800 border border-slate-700 p-4 text-sm text-slate-400">
            <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <p>
              <strong className="text-slate-300">Rates are never edited in place.</strong> Changing
              a rate adds a new version with its own start date and closes the previous one, so an
              invoice raised last year still reproduces under the rate that was in force when it was
              raised. Both versions stay in the history.
            </p>
          </div>

          {definitions.length === 0 ? (
            <div className="text-center py-12 bg-slate-800 border border-slate-700 text-slate-400">
              <p className="mb-2">No taxes are configured on this instance.</p>
              <p className="text-sm text-slate-500">
                Until at least one jurisdiction and one tax exist, invoices fall back to the flat
                percentage entered on each invoice.
              </p>
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <Th />
                    <Th>Tax</Th>
                    <Th>Jurisdiction</Th>
                    <Th>Applies to</Th>
                    <Th right>Current rate</Th>
                    <Th center>Verified</Th>
                    <Th right>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {definitions.map((definition) => (
                    <>
                      <tr key={definition.id} className="border-b border-slate-700">
                        <td className="pl-3">
                          <button
                            onClick={() => openHistory(definition)}
                            className="text-slate-500 hover:text-amber-500"
                            title="Version history"
                          >
                            {expanded === definition.id ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-100">{definition.name}</div>
                          <div className="text-xs text-slate-500">
                            {definition.code} &middot; {RATE_KIND_LABEL[definition.rateKind] ?? definition.rateKind}
                            {' · '}
                            <span
                              className={
                                definition.defaultTreatment === 'REMITTABLE'
                                  ? 'text-amber-500/80'
                                  : 'text-blue-400/80'
                              }
                            >
                              {definition.defaultTreatment === 'REMITTABLE' ? 'remittable' : 'recovered'}
                            </span>
                          </div>
                          {definition.suspension && (
                            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-red-500/20 text-red-300 border border-red-500/40">
                              <Ban className="w-3 h-3" />
                              Suspended — {definition.suspension.reason}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {definition.jurisdiction.name}
                          <span className="text-slate-600 text-xs ml-1">
                            ({definition.jurisdiction.level})
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {VOLUME_BASIS_LABEL[definition.volumeBasis] ?? definition.volumeBasis}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {definition.currentRate ? (
                            <>
                              <div className="text-slate-100 font-mono text-sm">
                                {definition.currentRate.formula
                                  ? definition.currentRate.formula
                                  : Number(definition.currentRate.rate).toFixed(6)}
                              </div>
                              <div className="text-xs text-slate-500">
                                from {definition.currentRate.effectiveFrom.slice(0, 10)}
                                {definition.versionCount > 1 && ` · ${definition.versionCount} versions`}
                              </div>
                            </>
                          ) : (
                            <span className="text-red-400 text-sm">No rate in force</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <VerifiedBadge date={definition.currentRate?.lastVerifiedAt ?? null} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                            {canEdit && (
                              <>
                                <SmallButton onClick={() => setNewVersionFor(definition)}>
                                  <Plus className="w-3 h-3" /> New rate
                                </SmallButton>
                                {definition.currentRate && (
                                  <SmallButton onClick={() => verifyRate(definition.currentRate!.id)}>
                                    <Check className="w-3 h-3" /> Mark verified
                                  </SmallButton>
                                )}
                                {definition.suspension ? (
                                  <SmallButton onClick={() => reinstate(definition.suspension!.id)}>
                                    Reinstate
                                  </SmallButton>
                                ) : (
                                  <SmallButton danger onClick={() => setSuspendFor(definition)}>
                                    Suspend
                                  </SmallButton>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {expanded === definition.id && (
                        <tr key={`${definition.id}-history`} className="border-b border-slate-700 bg-slate-900/50">
                          <td />
                          <td colSpan={6} className="px-4 py-3">
                            <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                              <History className="w-3.5 h-3.5" />
                              Rate history — every version ever in force, newest first
                            </div>
                            <table className="w-full text-sm">
                              <tbody>
                                {(history[definition.id] ?? []).map((version) => (
                                  <tr key={version.id} className="border-t border-slate-800">
                                    <td className="py-1.5 font-mono text-slate-300">
                                      {version.formula ?? Number(version.rate).toFixed(6)}
                                    </td>
                                    <td className="py-1.5 text-slate-400">
                                      {version.effectiveFrom.slice(0, 10)} &rarr;{' '}
                                      {version.effectiveTo ? version.effectiveTo.slice(0, 10) : 'current'}
                                    </td>
                                    <td className="py-1.5 text-slate-500 text-xs">
                                      {version.source ?? 'no source recorded'}
                                    </td>
                                    <td className="py-1.5 text-right">
                                      <VerifiedBadge date={version.lastVerifiedAt} />
                                    </td>
                                  </tr>
                                ))}
                                {(history[definition.id] ?? []).length === 0 && (
                                  <tr>
                                    <td className="py-2 text-slate-500">Loading…</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'certificates' && (
        <div className="bg-slate-800 border border-slate-700">
          {certificates.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              No exemption certificates on file.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-900 border-b border-slate-700">
                <tr>
                  <Th>Customer</Th>
                  <Th>Certificate</Th>
                  <Th>Type</Th>
                  <Th center>Expires</Th>
                  <Th center>Status</Th>
                </tr>
              </thead>
              <tbody>
                {certificates.map((certificate) => (
                  <tr key={certificate.id} className="border-b border-slate-700">
                    <td className="px-4 py-3 text-slate-100 text-sm">
                      {certificate.customer?.name ?? 'Unknown customer'}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm font-mono">
                      {certificate.certificateNumber}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{certificate.exemptionType}</td>
                    <td className="px-4 py-3 text-center text-slate-400 text-sm">
                      {certificate.expiresAt ? certificate.expiresAt.slice(0, 10) : 'No expiry'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 text-xs border ${
                          certificate.status === 'EXPIRED'
                            ? 'bg-red-500/20 text-red-300 border-red-500/50'
                            : certificate.status === 'EXPIRING_SOON'
                              ? 'bg-amber-500/20 text-amber-300 border-amber-500/50'
                              : 'bg-green-500/20 text-green-400 border-green-500/50'
                        }`}
                      >
                        {certificate.status === 'EXPIRED'
                          ? 'EXPIRED — not exempting'
                          : certificate.status === 'EXPIRING_SOON'
                            ? 'EXPIRING SOON'
                            : 'CURRENT'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'indices' && <IndexValues headers={headers} canEdit={canEdit} onError={setError} />}

      {newVersionFor && (
        <NewRateVersionModal
          definition={newVersionFor}
          headers={headers}
          onClose={() => setNewVersionFor(null)}
          onSaved={async () => {
            setNewVersionFor(null);
            setHistory({});
            setBanner('New rate version created. The previous version is closed, not replaced.');
            await load();
          }}
          onError={setError}
        />
      )}

      {suspendFor && (
        <SuspendModal
          definition={suspendFor}
          headers={headers}
          onClose={() => setSuspendFor(null)}
          onSaved={async () => {
            setSuspendFor(null);
            setBanner('Tax suspended. It will still appear on invoices as a zero-rated line.');
            await load();
          }}
          onError={setError}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ pieces */

function VerifiedBadge({ date }: { date: string | null }) {
  if (!date) {
    return <span className="text-xs text-red-400">Never verified</span>;
  }
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86_400_000);
  // A year is the point at which a rate has probably survived a legislative
  // session unchecked.
  const stale = days > 365;
  return (
    <span className={`text-xs ${stale ? 'text-amber-400' : 'text-slate-400'}`}>
      {date.slice(0, 10)}
      {stale && ' ⚠'}
    </span>
  );
}

function NewRateVersionModal({
  definition,
  headers,
  onClose,
  onSaved,
  onError,
}: {
  definition: TaxDefinitionRow;
  headers: (json?: boolean) => Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [rate, setRate] = useState(definition.currentRate?.rate ?? '');
  const [formula, setFormula] = useState(definition.currentRate?.formula ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [source, setSource] = useState('');
  const [saving, setSaving] = useState(false);
  const [formulaCheck, setFormulaCheck] = useState<string | null>(null);

  const isFormula = definition.rateKind === 'FORMULA';

  const checkFormula = async () => {
    const res = await fetch(`${API_URL}/api/tax/config/check-formula`, {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({ formula }),
    });
    const data = await res.json();
    setFormulaCheck(
      data.valid
        ? `Valid.${data.unknownReferences?.length ? ` Unknown references: ${data.unknownReferences.join(', ')}` : ''}`
        : `Invalid: ${extractMessage(data, 'the formula was rejected')}`
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/tax/config/definitions/${definition.id}/versions`, {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({
          rate: rate || '0',
          formula: isFormula ? formula : null,
          effectiveFrom,
          source: source || null,
        }),
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      onSaved();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`New rate for ${definition.name}`} onClose={onClose}>
      <div className="flex items-start space-x-3 bg-slate-900 border border-slate-700 p-3 mb-4 text-sm text-slate-400">
        <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p>
          This <strong className="text-slate-300">adds a version</strong> rather than changing the
          current one. The rate in force before this date stays exactly as it is, so invoices raised
          under it keep reproducing.
        </p>
      </div>

      <Field label="Effective from" hint="The date this rate starts applying. There is no default — a rate entered ahead of a legislative change must not take effect today by accident.">
        <input
          type="date"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm"
        />
      </Field>

      {isFormula ? (
        <Field label="Formula" hint="Arithmetic over published index values. Functions: min, max, round, floor, ceil, clamp.">
          <div className="flex gap-2">
            <input
              value={formula}
              onChange={(e) => {
                setFormula(e.target.value);
                setFormulaCheck(null);
              }}
              placeholder="max(0.17, round(wholesaleAverage * 0.07, 4))"
              className="flex-1 bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm font-mono"
            />
            <button
              onClick={checkFormula}
              className="px-3 py-2 border border-slate-600 text-slate-300 text-sm hover:border-amber-500"
            >
              Check
            </button>
          </div>
          {formulaCheck && (
            <p
              className={`mt-1 text-xs ${
                formulaCheck.startsWith('Valid') ? 'text-green-400' : 'text-red-400'
              }`}
            >
              {formulaCheck}
            </p>
          )}
        </Field>
      ) : null}

      <Field
        label={isFormula ? 'Fallback rate' : 'Rate'}
        hint={
          isFormula
            ? 'Used if the formula cannot be evaluated — so a mistyped expression degrades to a number you chose rather than to zero.'
            : definition.rateKind === 'PERCENTAGE'
              ? 'A percentage, e.g. 4 for 4%.'
              : 'Dollars per unit, e.g. 0.244 for 24.4 cents per gallon.'
        }
      >
        <input
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm font-mono"
        />
      </Field>

      <Field label="Source" hint="Where this number came from — a statute cite, a bulletin, a URL. The first question after 'is this right?' is 'says who?'.">
        <input
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="e.g. HB 1234, 2026 session"
          className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm"
        />
      </Field>

      <div className="flex gap-3 mt-6">
        <ModalButton onClick={onClose} disabled={saving}>
          Cancel
        </ModalButton>
        <ModalButton primary onClick={save} disabled={saving || !effectiveFrom}>
          {saving ? 'Saving…' : 'Add version'}
        </ModalButton>
      </div>
    </Modal>
  );
}

function SuspendModal({
  definition,
  headers,
  onClose,
  onSaved,
  onError,
}: {
  definition: TaxDefinitionRow;
  headers: (json?: boolean) => Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [suspendedFrom, setSuspendedFrom] = useState('');
  const [suspendedTo, setSuspendedTo] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/tax/config/definitions/${definition.id}/suspend`, {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({
          suspendedFrom,
          suspendedTo: suspendedTo || null,
          reason,
        }),
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      onSaved();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to suspend');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Suspend ${definition.name}`} onClose={onClose}>
      <div className="flex items-start space-x-3 bg-slate-900 border border-slate-700 p-3 mb-4 text-sm text-slate-400">
        <ShieldCheck className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p>
          Suspending is <strong className="text-slate-300">not</strong> the same as setting the rate
          to zero. The rate is kept and switched off for the period, the tax still appears on
          invoices as a zero-rated line with your reason, and it reinstates at the original rate
          when the suspension ends.
        </p>
      </div>

      <Field label="Suspended from">
        <input
          type="date"
          value={suspendedFrom}
          onChange={(e) => setSuspendedFrom(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm"
        />
      </Field>
      <Field label="Suspended until" hint="Leave empty if the end date has not been announced yet.">
        <input
          type="date"
          value={suspendedTo}
          onChange={(e) => setSuspendedTo(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm"
        />
      </Field>
      <Field label="Reason" hint="Appears on every invoice line and in the calculation trace. Required.">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. HB 1234 fuel tax holiday"
          className="w-full bg-slate-900 border border-slate-700 focus:border-amber-500 px-3 py-2 text-slate-100 text-sm"
        />
      </Field>

      <div className="flex gap-3 mt-6">
        <ModalButton onClick={onClose} disabled={saving}>
          Cancel
        </ModalButton>
        <ModalButton primary danger onClick={save} disabled={saving || !suspendedFrom || !reason}>
          {saving ? 'Saving…' : 'Suspend'}
        </ModalButton>
      </div>
    </Modal>
  );
}

function IndexValues({
  headers,
  canEdit,
  onError,
}: {
  headers: (json?: boolean) => Record<string, string>;
  canEdit: boolean;
  onError: (message: string) => void;
}) {
  const [values, setValues] = useState<
    { id: string; code: string; name: string; value: string; effectiveFrom: string; effectiveTo: string | null; source: string | null }[]
  >([]);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API_URL}/api/tax/config/indices`, { headers: headers() });
      if (res.ok) setValues((await res.json()).values);
      else onError(await parseApiError(res));
    })();
  }, [headers, onError]);

  return (
    <div className="space-y-3">
      <div className="flex items-start space-x-3 bg-slate-800 border border-slate-700 p-4 text-sm text-slate-400">
        <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <p>
          Published figures that indexed rates are calculated from — an average wholesale price, a
          CPI figure. Around half the states tie part of their motor fuel rate to one of these.
          Update the value when your state publishes it, exactly as you would a rate; the old value
          is kept so past invoices still reproduce.
        </p>
      </div>

      <div className="bg-slate-800 border border-slate-700">
        {values.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">
            No index values published. Only needed if a tax uses a formula rate.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-900 border-b border-slate-700">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th right>Value</Th>
                <Th>In force</Th>
                <Th>Source</Th>
              </tr>
            </thead>
            <tbody>
              {values.map((value) => (
                <tr key={value.id} className="border-b border-slate-700">
                  <td className="px-4 py-3 font-mono text-sm text-amber-500">{value.code}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{value.name}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-slate-100">
                    {Number(value.value).toFixed(6)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-400">
                    {value.effectiveFrom.slice(0, 10)} &rarr;{' '}
                    {value.effectiveTo ? value.effectiveTo.slice(0, 10) : 'current'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{value.source ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!canEdit && (
        <p className="text-xs text-slate-500">
          Only an administrator can publish new index values.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- atoms */

function Th({ children, center, right }: { children?: React.ReactNode; center?: boolean; right?: boolean }) {
  return (
    <th
      className={`${center ? 'text-center' : right ? 'text-right' : 'text-left'} px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium`}
    >
      {children}
    </th>
  );
}

function SmallButton({
  children,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs border transition-colors ${
        danger
          ? 'border-red-500/50 text-red-400 hover:bg-red-500/10'
          : 'border-slate-600 text-slate-300 hover:border-amber-500 hover:text-amber-500'
      }`}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <label className="block text-sm text-slate-300 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-slate-800 border border-slate-700 p-6 max-w-lg w-full my-8">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalButton({
  children,
  onClick,
  primary,
  danger,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  primary?: boolean;
  danger?: boolean;
  disabled?: boolean;
}) {
  const style = !primary
    ? 'border border-slate-600 text-slate-300 hover:bg-slate-700'
    : danger
      ? 'bg-red-600 text-white hover:bg-red-700'
      : 'bg-amber-500 text-slate-900 hover:bg-amber-600';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex-1 px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${style}`}
    >
      {children}
    </button>
  );
}
